import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderSummaryCardStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    tableHeaders?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    usernameCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    emailCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    roleCells?: DataTableCellProps;
    lastSignInCells?: DataTableCellProps;
    noQuotesText?: TypographyPresentationProps;
    noQuotesGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    viewAllButton?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    container: {
        css: css`
            overflow-x: auto;
            margin-bottom: 25px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    usernameCells: {
        css: css`
            font-weight: 400;
        `,
    },
    noQuotesGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    noQuotesText: { variant: "h3" },
    dataTable: {
        css: css`
            border: 1px solid #ddd;
            th,
            td {
                border-right: 1px solid #ddd;
            }
        `,
    },
    tableHeaders: {
        css: css`
            border-bottom: 2px solid #d0d0d0;
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    dataTableBody: {
        css: css`
            tr,
            td,
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 0 0 15px 0;
            justify-content: space-between;
        `,
    },
    titleText: {
        variant: "h3",
    },
    viewAllButton: {
        css: css`
            background-color: #999;
            padding: 7px 11px;
            :hover {
                opacity: 0.9;
                span {
                    text-decoration: none;
                }
            }
            span {
                color: #fff !important;
                font-size: 14px;
                font-weight: 700;
            }
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = orderSummaryCardStyles;

class RecentOrdersTable extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            <>
                <StyledWrapper {...styles.container} data-test-selector="userListUsersTable">
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.titleText}>Recent Quotes</Typography>
                        <Link href={myQuotesPageUrl} {...styles.viewAllButton}>
                            View All
                        </Link>
                    </GridItem>
                    {quotesDataView.value.length === 0 && (
                        <GridItem {...styles.noQuotesGridItem}>
                            <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                        </GridItem>
                    )}
                    {quotesDataView.value.length !== 0 && (
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_usernameHeader"
                                >
                                    {translate("Quote #")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_nameHeader"
                                >
                                    {translate("Status")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_emailHeader"
                                >
                                    {translate("Requested")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_statusHeader"
                                >
                                    {translate("Expires")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_roleHeader"
                                >
                                    {translate("Customer")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_lastSignInHeader"
                                >
                                    {translate("Ship To / Pick Up")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(quote => {
                                    if (!quote) {
                                        return null;
                                    }

                                    const orderDateDisplay = getLocalizedDateTime({
                                        dateTime: quote.orderDate!,
                                        language: this.props.session.language,
                                        options: {
                                            year: "numeric",
                                            month: "numeric",
                                            day: "numeric",
                                        },
                                    });

                                    let expirationDateDisplay = "";
                                    if (quote.expirationDate) {
                                        expirationDateDisplay = getLocalizedDateTime({
                                            dateTime: quote.expirationDate,
                                            language: this.props.session.language,
                                            options: {
                                                year: "numeric",
                                                month: "numeric",
                                                day: "numeric",
                                            },
                                        });
                                    }
                                    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;
                                    return (
                                        <DataTableRow
                                            {...styles.dataTableRow}
                                            key={quote.id}
                                            data-test-selector="userListUsersTable_row"
                                            data-test-key={quote.id}
                                        >
                                            <DataTableCell
                                                {...styles.usernameCells}
                                                as="th"
                                                scope="row"
                                                data-test-selector="userListUsersTable_cell_username"
                                                data-test-key={quote.id}
                                            >
                                                {quote.quoteNumber && (
                                                    <RfqQuoteDetailsPageTypeLink
                                                        title={quote.quoteNumber}
                                                        quoteId={quote.id}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.nameCells}
                                                data-test-selector="userListUsersTable_cell_name"
                                                data-test-key={quote.id}
                                            >
                                                {quote.statusDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.emailCells}
                                                data-test-selector="userListUsersTable_cell_email"
                                                data-test-key={quote.id}
                                            >
                                                {orderDateDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="userListUsersTable_cell_status"
                                                data-test-key={quote.id}
                                            >
                                                {expirationDateDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.roleCells}
                                                data-test-selector="userListUsersTable_cell_role"
                                                data-test-key={quote.id}
                                            >
                                                {customerDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.lastSignInCells}
                                                data-test-selector="userListUsersTable_cell_lastSignIn"
                                                data-test-key={quote.id}
                                            >
                                                {quote.shipToFullAddress}
                                            </DataTableCell>
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    )}
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrdersTable),
    definition: {
        group: "Common",
        icon: "list-ul",
        displayName: "Recent Quotes Table View",
    },
};

export default widgetModule;
